import { useState } from 'react';
import { ComparisonData } from '../types';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

interface ComparisonPanelProps {
  currentConsultationId: string;
  onCompare: (previousConsultationId: string) => void;
}

const ComparisonPanel = ({ currentConsultationId, onCompare }: ComparisonPanelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState('');
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);

  const previousConsultations = [
    { value: 'CONS-2024-001', label: 'Consulta 15/01/2024 - 45 produtos' },
    { value: 'CONS-2024-002', label: 'Consulta 10/01/2024 - 38 produtos' },
    { value: 'CONS-2024-003', label: 'Consulta 05/01/2024 - 52 produtos' },
    { value: 'CONS-2023-125', label: 'Consulta 28/12/2023 - 41 produtos' }
  ];

  const handleCompare = () => {
    if (!selectedConsultation) return;

    const mockComparisonData: ComparisonData = {
      previousConsultationId: selectedConsultation,
      rateChanges: [
        {
          productCode: 'PROD-001',
          oldRate: 18.5,
          newRate: 19.2,
          difference: 0.7
        },
        {
          productCode: 'PROD-005',
          oldRate: 12.0,
          newRate: 11.5,
          difference: -0.5
        },
        {
          productCode: 'PROD-012',
          oldRate: 25.3,
          newRate: 26.1,
          difference: 0.8
        }
      ],
      regulatoryUpdates: [
        'Atualização da alíquota de ICMS para produtos eletrônicos em SP - Decreto 67.123/2024',
        'Nova isenção de IPI para produtos sustentáveis - Portaria 456/2024'
      ]
    };

    setComparisonData(mockComparisonData);
    onCompare(selectedConsultation);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="GitCompare" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">Comparação com Consultas Anteriores</h3>
        </div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 hover:bg-muted rounded-md transition-colors"
          aria-label={isOpen ? 'Recolher' : 'Expandir'}
        >
          <Icon
            name={isOpen ? 'ChevronUp' : 'ChevronDown'}
            size={20}
            color="var(--color-muted-foreground)"
          />
        </button>
      </div>

      {isOpen && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Select
                options={previousConsultations}
                value={selectedConsultation}
                onChange={setSelectedConsultation}
                placeholder="Selecione uma consulta anterior"
              />
            </div>
            <Button
              variant="default"
              onClick={handleCompare}
              disabled={!selectedConsultation}
              iconName="GitCompare"
              iconPosition="left"
            >
              Comparar
            </Button>
          </div>

          {comparisonData && (
            <div className="space-y-4 pt-4 border-t border-border">
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3">
                  Alterações de Alíquotas
                </h4>
                <div className="space-y-2">
                  {comparisonData.rateChanges.map((change, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-muted rounded-md"
                    >
                      <span className="text-sm font-mono text-foreground">
                        {change.productCode}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-mono text-muted-foreground">
                          {change.oldRate.toFixed(2)}%
                        </span>
                        <Icon name="ArrowRight" size={16} color="var(--color-muted-foreground)" />
                        <span className="text-sm font-mono text-foreground font-medium">
                          {change.newRate.toFixed(2)}%
                        </span>
                        <span
                          className={`text-sm font-mono font-semibold ${
                            change.difference > 0 ? 'text-error' : 'text-success'
                          }`}
                        >
                          {change.difference > 0 ? '+' : ''}
                          {change.difference.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {comparisonData.regulatoryUpdates.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">
                    Atualizações Regulatórias
                  </h4>
                  <div className="space-y-2">
                    {comparisonData.regulatoryUpdates.map((update, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-3 bg-accent/10 border border-accent/20 rounded-md"
                      >
                        <Icon
                          name="Info"
                          size={16}
                          color="var(--color-accent)"
                          className="mt-0.5 flex-shrink-0"
                        />
                        <p className="text-sm text-foreground leading-relaxed">{update}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComparisonPanel;